import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';

const G = "#004D35";
const MINT = "#a8f0c6";
const BORDER = "#e8e5e0";
const RED = "#c0392b";
const RED_BG = "#fdf2f2";
const RED_BORDER = "#f5c6c2";
const YELLOW = "#f5a623";

function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#0a1a0f", color: "#fff", padding: "12px 28px", borderRadius: 999, fontSize: 14, zIndex: 600, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}>
      {msg}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:    { label: "Active",    color: G,         bg: MINT },
    paused:    { label: "Paused",    color: "#7a5a00", bg: "#ffd97d" },
    cancelled: { label: "Cancelled", color: "#555",    bg: "#e0ddd8" },
    suspended: { label: "Suspended", color: RED,       bg: RED_BG, border: RED_BORDER },
    pending:   { label: "Pending",   color: "#555",    bg: "#e0ddd8" },
  };
  const s = map[status] || { label: status, color: "#555", bg: "#e0ddd8" };
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, border: s.border ? `1px solid ${s.border}` : "none", padding: "3px 10px", borderRadius: 999, display: "inline-block" }}>
      {s.label}
    </span>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(10,26,15,0.5)", backdropFilter: "blur(6px)" }} />
      <div style={{ position: "relative", background: "#fff", borderRadius: 20, width: "100%", maxWidth: 460, boxShadow: "0 32px 80px rgba(0,0,0,0.2)", zIndex: 1, padding: "32px 32px 28px" }}>
        {children}
      </div>
    </div>
  );
}

function AdminActionModal({ advisor, action, onConfirm, onClose, loading }) {
  const map = {
    unpause:   { title: `Unpause ${advisor?.name}?`,              body: "Their listing will become immediately visible to business owners and billing will resume.",                        cta: "Unpause account",    color: G   },
    suspend:   { title: `Suspend ${advisor?.name}?`,              body: "Their listing will be immediately hidden. They will be notified by email. You can unsuspend at any time.",       cta: "Suspend account",    color: RED },
    unsuspend: { title: `Remove suspension for ${advisor?.name}?`, body: "Their listing will become active and visible again immediately.",                                               cta: "Remove suspension",  color: G   },
    delete:    { title: `Delete ${advisor?.name}'s account?`,     body: "This will permanently remove their profile, cancel their subscription, and delete all data. Cannot be undone.", cta: "Delete account",     color: RED },
  };
  const m = map[action];
  if (!m) return null;
  return (
    <Modal onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif", margin: "0 0 10px" }}>{m.title}</h2>
      <p style={{ fontSize: 14, color: "#666", lineHeight: 1.6, margin: "0 0 24px" }}>{m.body}</p>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onClose} disabled={loading} style={{ flex: 1, background: "#fff", color: "#888", border: `1.5px solid ${BORDER}`, padding: "12px", borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={{ flex: 1, background: m.color, color: "#fff", border: "none", padding: "12px", borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Working...' : m.cta}
        </button>
      </div>
    </Modal>
  );
}

function DeleteOwnerModal({ owner, onConfirm, onClose, loading }) {
  if (!owner) return null;
  return (
    <Modal onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif", margin: "0 0 10px" }}>Delete {owner.name}'s account?</h2>
      <p style={{ fontSize: 14, color: "#666", lineHeight: 1.6, margin: "0 0 24px" }}>This will permanently remove their account and all saved advisors. Cannot be undone.</p>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onClose} disabled={loading} style={{ flex: 1, background: "#fff", color: "#888", border: `1.5px solid ${BORDER}`, padding: "12px", borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={{ flex: 1, background: RED, color: "#fff", border: "none", padding: "12px", borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Deleting...' : 'Delete account'}
        </button>
      </div>
    </Modal>
  );
}

function getAdvisorDisplayStatus(a) {
  if (a.suspended_at) return 'suspended';
  if (a.pause_started_at) return 'paused';
  if (a.status === 'approved') return 'active';
  if (a.status === 'pending') return 'pending';
  return a.status || 'pending';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const AdminSubscriptions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("advisors");
  const [advisors, setAdvisors] = useState([]);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [ownerDeleteModal, setOwnerDeleteModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [globalPrice, setGlobalPrice] = useState('');
  const [isBetaMode, setIsBetaMode] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const fetchAdvisors = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/advisors', { credentials: 'include' });
      if (response.ok) setAdvisors(await response.json());
    } catch (error) {
      console.error('Error fetching advisors:', error);
    }
  }, []);

  const fetchOwners = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/business-owners', { credentials: 'include' });
      if (response.ok) setOwners(await response.json());
    } catch (error) {
      console.error('Error fetching business owners:', error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/settings', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setGlobalPrice(data.global_monthly_price || '0');
        setIsBetaMode(data.beta_mode);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    Promise.all([fetchAdvisors(), fetchOwners(), fetchSettings()]).finally(() => setLoading(false));
  }, [user, navigate, fetchAdvisors, fetchOwners, fetchSettings]);

  const handleSavePrice = async () => {
    setSavingPrice(true);
    try {
      const response = await fetch('/api/admin/settings/pricing', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ global_monthly_price: globalPrice })
      });
      if (response.ok) showToast('Global price updated successfully');
      else showToast('Failed to update price');
    } catch (error) {
      console.error('Error saving price:', error);
      showToast('Error saving price');
    } finally {
      setSavingPrice(false);
    }
  };

  const confirmAdvisorAction = async () => {
    const { advisor, action } = modal;
    setActionLoading(true);
    try {
      let url, method, body;

      if (action === 'suspend') {
        url = `/api/admin/advisors/${advisor.id}/suspend`;
        method = 'POST';
        body = { reason: 'Suspended by admin' };
      } else if (action === 'unsuspend') {
        url = `/api/admin/advisors/${advisor.id}/unsuspend`;
        method = 'POST';
        body = {};
      } else if (action === 'unpause') {
        url = `/api/admin/advisors/${advisor.id}/status`;
        method = 'PUT';
        body = { status: 'active' };
      } else if (action === 'delete') {
        url = `/api/admin/advisors/${advisor.id}`;
        method = 'DELETE';
        body = {};
      }

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const msgs = {
          unpause: `${advisor.name} unpaused`,
          suspend: `${advisor.name} suspended`,
          unsuspend: `Suspension removed for ${advisor.name}`,
          delete: `${advisor.name} deleted`
        };
        showToast(msgs[action]);
        await fetchAdvisors();
      } else {
        const err = await response.json().catch(() => ({}));
        showToast(err.error || `Failed to ${action} advisor`);
      }
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      showToast(`Error: ${action} failed`);
    } finally {
      setActionLoading(false);
      setModal(null);
    }
  };

  const confirmDeleteOwner = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/admin/business-owners/${ownerDeleteModal.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        showToast('Business owner account deleted');
        await fetchOwners();
      } else {
        showToast('Failed to delete owner');
      }
    } catch (error) {
      console.error('Error deleting owner:', error);
      showToast('Error deleting owner');
    } finally {
      setActionLoading(false);
      setOwnerDeleteModal(null);
    }
  };

  const filteredAdvisors = advisors.filter(a => {
    const displayStatus = getAdvisorDisplayStatus(a);
    const name = a.name || '';
    const email = a.email || '';
    const matchSearch = !search || name.toLowerCase().includes(search.toLowerCase()) || email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || displayStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const activeCount = advisors.filter(a => getAdvisorDisplayStatus(a) === 'active').length;
  const pausedCount = advisors.filter(a => getAdvisorDisplayStatus(a) === 'paused').length;
  const suspendedCount = advisors.filter(a => getAdvisorDisplayStatus(a) === 'suspended').length;

  const stats = [
    { label: "Total Advisors",  value: advisors.length },
    { label: "Active",          value: activeCount,    color: G },
    { label: "Paused",          value: pausedCount,    color: YELLOW },
    { label: "Suspended",       value: suspendedCount, color: RED },
    { label: "Business Owners", value: owners.length },
    { label: "MRR",             value: `$${activeCount * Number(globalPrice || 0)}`, color: G },
  ];

  if (loading) {
    return (
      <AdminLayout activeTab="subscriptions">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
          <div style={{ width: 32, height: 32, border: "3px solid #e0e0e0", borderTopColor: G, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab="subscriptions">
      <Toast msg={toast} />
      {modal && (
        <AdminActionModal
          advisor={modal.advisor}
          action={modal.action}
          onConfirm={confirmAdvisorAction}
          onClose={() => setModal(null)}
          loading={actionLoading}
        />
      )}
      {ownerDeleteModal && (
        <DeleteOwnerModal
          owner={ownerDeleteModal}
          onConfirm={confirmDeleteOwner}
          onClose={() => setOwnerDeleteModal(null)}
          loading={actionLoading}
        />
      )}

      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing & Subscriptions</h1>

        {/* Global Pricing Section */}
        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm space-y-4">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Global Subscription Pricing</h2>

          {isBetaMode && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 text-amber-800 px-4 py-3 rounded-xl text-sm font-medium">
              <span className="text-lg">⚠</span>
              Beta Mode is ON — all new approvals get $0 subscriptions.
            </div>
          )}

          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-xs">
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Current global rate:</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                <input
                  type="number"
                  value={globalPrice}
                  onChange={(e) => setGlobalPrice(e.target.value)}
                  className="w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl font-bold text-gray-900 focus:bg-white focus:border-green-600 outline-none transition-all"
                  placeholder="0.00"
                />
              </div>
            </div>
            <button
              onClick={handleSavePrice}
              disabled={savingPrice}
              className="px-8 py-3 bg-green-900 hover:bg-green-800 text-white font-bold rounded-xl transition-all disabled:opacity-50"
            >
              {savingPrice ? 'Saving...' : 'Save'}
            </button>
          </div>
          <p className="text-xs text-gray-400 font-medium italic">
            Default monthly rate for all paid advisors. Beta/Comped plans are not affected.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {stats.map((s, i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <div style={{ fontSize: 26, fontWeight: 700, color: s.color || "#0a1a0f", fontFamily: "Georgia, serif" }}>{s.value}</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 4, background: "#f0ede8", borderRadius: 999, padding: 4, width: "fit-content" }}>
          {[["advisors", "Advisors"], ["owners", "Business Owners"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: "8px 20px", borderRadius: 999, background: tab === id ? "#fff" : "transparent", color: tab === id ? "#0a1a0f" : "#888", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease", boxShadow: tab === id ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Advisors tab */}
        {tab === "advisors" && (
          <div>
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="border border-gray-200 rounded-xl text-sm px-4 py-2 outline-none focus:border-green-700"
                style={{ width: 260 }}
              />
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {["all", "active", "paused", "suspended", "pending"].map(s => (
                  <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: "6px 14px", borderRadius: 999, border: `1.5px solid ${statusFilter === s ? G : BORDER}`, background: statusFilter === s ? "rgba(0,77,53,0.06)" : "#fff", color: statusFilter === s ? G : "#888", fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "capitalize" }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                    {["Advisor", "Category", "Status", "Joined", "Next Billing", "Actions"].map(h => (
                      <th key={h} style={{ padding: "12px 16px", fontSize: 10, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "left" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredAdvisors.map((a, i) => {
                    const displayStatus = getAdvisorDisplayStatus(a);
                    return (
                      <tr key={a.id} style={{ borderBottom: i < filteredAdvisors.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: "#0a1a0f" }}>{a.name || '—'}</div>
                          <div style={{ fontSize: 12, color: "#aaa" }}>{a.email}</div>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: 12, color: "#888", textTransform: "capitalize" }}>{a.category_slug || a.sub_plan_type || '—'}</td>
                        <td style={{ padding: "14px 16px" }}>
                          <StatusBadge status={displayStatus} />
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: 12, color: "#aaa" }}>{formatDate(a.user_created_at)}</td>
                        <td style={{ padding: "14px 16px", fontSize: 12, color: "#aaa" }}>
                          {a.current_period_end ? formatDate(a.current_period_end) : <span style={{ color: "#ccc" }}>—</span>}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {displayStatus === "paused" && (
                              <button onClick={() => setModal({ advisor: a, action: "unpause" })} style={{ fontSize: 11, fontWeight: 700, color: G, background: "rgba(0,77,53,0.08)", border: "1px solid rgba(0,77,53,0.2)", padding: "5px 12px", borderRadius: 999, cursor: "pointer" }}>Unpause</button>
                            )}
                            {displayStatus === "suspended" && (
                              <button onClick={() => setModal({ advisor: a, action: "unsuspend" })} style={{ fontSize: 11, fontWeight: 700, color: G, background: "rgba(0,77,53,0.08)", border: "1px solid rgba(0,77,53,0.2)", padding: "5px 12px", borderRadius: 999, cursor: "pointer" }}>Unsuspend</button>
                            )}
                            {(displayStatus === "active" || displayStatus === "paused") && (
                              <button onClick={() => setModal({ advisor: a, action: "suspend" })} style={{ fontSize: 11, fontWeight: 700, color: "#a07030", background: "rgba(245,166,35,0.1)", border: "1px solid rgba(245,166,35,0.3)", padding: "5px 12px", borderRadius: 999, cursor: "pointer" }}>Suspend</button>
                            )}
                            <button onClick={() => setModal({ advisor: a, action: "delete" })} style={{ fontSize: 11, fontWeight: 700, color: RED, background: RED_BG, border: `1px solid ${RED_BORDER}`, padding: "5px 12px", borderRadius: 999, cursor: "pointer" }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredAdvisors.length === 0 && (
                <div style={{ textAlign: "center", padding: 40, color: "#bbb", fontSize: 14 }}>No advisors found</div>
              )}
            </div>
          </div>
        )}

        {/* Business owners tab */}
        {tab === "owners" && (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                  {["Business Owner", "Joined", "Saved Advisors", "Actions"].map(h => (
                    <th key={h} style={{ padding: "12px 16px", fontSize: 10, fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {owners.map((o, i) => (
                  <tr key={o.id} style={{ borderBottom: i < owners.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                    <td style={{ padding: "14px 16px" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#0a1a0f" }}>{o.name || '—'}</div>
                      <div style={{ fontSize: 12, color: "#aaa" }}>{o.email}</div>
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 12, color: "#aaa" }}>{formatDate(o.created_at)}</td>
                    <td style={{ padding: "14px 16px", fontSize: 13, color: "#555" }}>{o.saved_advisors_count || 0}</td>
                    <td style={{ padding: "14px 16px" }}>
                      <button onClick={() => setOwnerDeleteModal(o)} style={{ fontSize: 11, fontWeight: 700, color: RED, background: RED_BG, border: `1px solid ${RED_BORDER}`, padding: "5px 12px", borderRadius: 999, cursor: "pointer" }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {owners.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: "#bbb", fontSize: 14 }}>No business owners found</div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSubscriptions;
